/**
 * Hotfix 5c — lista de seções = categorias REAIS dos produtos (não só departments manuais).
 * Uma varredura leve por vendorId (só lê category), sem reescrever documentos.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { compareDepartments } from '../utils/catalogOrder';

const SCAN_BATCH = 100;

export interface UseVendorCategorySectionsResult {
  /** Categorias não vazias encontradas nos produtos */
  sections: string[];
  /** Existe pelo menos um produto sem category */
  hasUncategorized: boolean;
  loading: boolean;
  ready: boolean;
  productCount: number;
  error: string | null;
}

export function useVendorCategorySections(
  storeId: string | null,
  ordemDepartamentos?: Record<string, number> | null
): UseVendorCategorySectionsResult {
  const [rawSections, setRawSections] = useState<string[]>([]);
  const [hasUncategorized, setHasUncategorized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [productCount, setProductCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!storeId) {
      setRawSections([]);
      setHasUncategorized(false);
      setLoading(false);
      setReady(false);
      setProductCount(0);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setReady(false);
    setError(null);

    (async () => {
      const found = new Set<string>();
      let empty = false;
      let total = 0;
      let cursor: QueryDocumentSnapshot<DocumentData> | null = null;

      try {
        for (;;) {
          const q = cursor
            ? query(
                collection(db, 'products'),
                where('vendorId', '==', storeId),
                orderBy('__name__'),
                startAfter(cursor),
                limit(SCAN_BATCH)
              )
            : query(
                collection(db, 'products'),
                where('vendorId', '==', storeId),
                orderBy('__name__'),
                limit(SCAN_BATCH)
              );
          const snap = await getDocs(q);
          if (cancelled) return;
          if (snap.empty) break;

          for (const doc of snap.docs) {
            total += 1;
            cursor = doc;
            const raw = doc.data()?.category;
            const cat = typeof raw === 'string' ? raw.trim() : '';
            if (!cat) empty = true;
            else found.add(cat);
          }
          if (snap.size < SCAN_BATCH) break;
        }

        if (cancelled) return;
        setRawSections(Array.from(found));
        setHasUncategorized(empty);
        setProductCount(total);
        setLoading(false);
        setReady(true);
      } catch (err: any) {
        if (cancelled) return;
        console.error('[useVendorCategorySections]', err);
        setError(err?.message || 'Erro ao listar seções');
        setLoading(false);
        setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storeId]);

  const sections = useMemo(
    () =>
      [...rawSections].sort((a, b) =>
        compareDepartments(a, b, ordemDepartamentos || undefined)
      ),
    [rawSections, ordemDepartamentos]
  );

  return { sections, hasUncategorized, loading, ready, productCount, error };
}

/** Une listas de seções sem duplicar (trim). */
export function mergeSectionLists(...lists: Array<string[] | null | undefined>): string[] {
  const set = new Set<string>();
  for (const list of lists) {
    for (const s of list || []) {
      const t = String(s || '').trim();
      if (t) set.add(t);
    }
  }
  return Array.from(set);
}
